import { supabase } from "@/services/supabase";
import { nanoid } from "nanoid";
import { isCourseFulfilledByUser } from "./progressService";

export interface Certificate {
  id?: string;
  user_id: string;
  course_id: string;
  recipient_name: string;
  issue_date: string;
  certificate_id: string;
  is_valid: boolean;
  created_at?: string;
  updated_at?: string;
  nft_token_id?: string;
  nft_transaction_hash?: string;
  blockchain_network?: string;
  metadata_url?: string;
  image_url?: string;
  recipient_wallet_address?: string;
  course_name?: string;
  blockchain_verification_url?: string;
}

/**
 * Tạo chứng chỉ mới cho khóa học
 */
export const createCertificate = async (
  courseId: string,
  recipientName: string,
  totalNodes: number,
): Promise<Certificate | null> => {
  try {
    // Kiểm tra courseId hợp lệ
    if (
      !courseId ||
      courseId === "[object Object]" ||
      typeof courseId !== "string"
    ) {
      console.error("Invalid courseId provided:", courseId);
      return null;
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return null;

    try {
      // Đơn giản hóa: Không còn kiểm tra tiến độ, chỉ tạo chứng chỉ
      // Nếu người dùng nhấn nút tạo chứng chỉ, chúng ta cho phép tạo

      // Kiểm tra xem người dùng đã có chứng chỉ cho khóa học này chưa
      const { data: existingCertificate, error } = await supabase
        .from("certificates")
        .select("*")
        .eq("user_id", user.user.id)
        .eq("course_id", courseId)
        .single();

      if (error) {
        // Kiểm tra nếu lỗi là do bảng không tồn tại
        if (error.code === "42P01") {
          // Bảng certificates chưa tồn tại
          console.warn("Bảng certificates chưa được tạo trong cơ sở dữ liệu");

          // Tạo chứng chỉ tạm thời trong localStorage
          const tempCertificate = {
            id: `temp-${Date.now()}`,
            user_id: user.user.id,
            course_id: courseId,
            recipient_name: recipientName,
            certificate_id: `TEMP-CERT-${Date.now()}`,
            issue_date: new Date().toISOString(),
            is_valid: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          localStorage.setItem(
            `temp-certificate-${courseId}`,
            JSON.stringify(tempCertificate),
          );
          return tempCertificate as Certificate;
        }

        // Kiểm tra nếu lỗi là do không tìm thấy bản ghi
        if (error.code === "PGRST116") {
          // Đây là trường hợp bình thường khi người dùng chưa có chứng chỉ
          console.log("Người dùng chưa có chứng chỉ, tiếp tục tạo mới");
          // Tiếp tục tạo chứng chỉ mới
        } else {
          console.error("Error checking existing certificate:", error);
          return null;
        }
      } else if (existingCertificate) {
        return existingCertificate as Certificate;
      }

      // Tạo ID chứng chỉ duy nhất
      const certificateId = `CERT-${nanoid(10)}`;

      // Tạo chứng chỉ mới
      const { data, error: insertError } = await supabase
        .from("certificates")
        .insert({
          user_id: user.user.id,
          course_id: courseId,
          recipient_name: recipientName,
          certificate_id: certificateId,
          issue_date: new Date().toISOString(),
          is_valid: true,
        })
        .select()
        .single();

      if (insertError) {
        console.error("Error creating certificate:", insertError);
        return null;
      }

      return data as Certificate;
    } catch (error) {
      console.error("Error checking certificate:", error);

      // Tạo chứng chỉ tạm thời trong localStorage
      const tempCertificate = {
        id: `temp-${Date.now()}`,
        user_id: user.user.id,
        course_id: courseId,
        recipient_name: recipientName,
        certificate_id: `TEMP-CERT-${Date.now()}`,
        issue_date: new Date().toISOString(),
        is_valid: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      localStorage.setItem(
        `temp-certificate-${courseId}`,
        JSON.stringify(tempCertificate),
      );
      return tempCertificate as Certificate;
    }
  } catch (error) {
    console.error("Error in createCertificate:", error);

    // Trong trường hợp gặp lỗi, vẫn tạo chứng chỉ tạm thời trong localStorage
    const { data: user } = await supabase.auth.getUser();
    if (user.user) {
      const tempCertificate = {
        id: `temp-${Date.now()}`,
        user_id: user.user.id,
        course_id: courseId,
        recipient_name: recipientName,
        certificate_id: `TEMP-CERT-${Date.now()}`,
        issue_date: new Date().toISOString(),
        is_valid: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      localStorage.setItem(
        `temp-certificate-${courseId}`,
        JSON.stringify(tempCertificate),
      );
      return tempCertificate as Certificate;
    }

    return null;
  }
};

/**
 * Lấy certificate cho user và khóa học hiện tại
 */
export const getUserCertificate = async (courseId: string) => {
  try {
    // Lấy thông tin user
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error("Người dùng chưa đăng nhập");
    }

    // Thử lấy thông tin chứng chỉ từ API
    try {
      // Lấy session hiện tại
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error("Người dùng chưa đăng nhập");
      }

      // Lấy thông tin certificate từ API
      const response = await fetch(
        `/api/certificates/user-certificate?courseId=${courseId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      // Nếu API trả về 404 hoặc lỗi khác, sẽ tiếp tục với phương án dự phòng
      console.log(
        `API không trả về certificate (status: ${response.status}), thử phương án dự phòng`,
      );
    } catch (apiError) {
      console.log("Lỗi khi gọi API certificates:", apiError);
      // Tiếp tục với phương án dự phòng
    }

    // Phương án dự phòng: Truy vấn trực tiếp từ Supabase
    console.log("Thử lấy certificate trực tiếp từ Supabase...");
    const { data: certificateData, error: certError } = await supabase
      .from("certificates")
      .select("*, courses(name)")
      .eq("user_id", user.user.id)
      .eq("course_id", courseId)
      .single();

    if (certError) {
      if (certError.code === "PGRST116") {
        console.log("Không tìm thấy certificate trong database");
        return null; // Không tìm thấy certificate
      }
      throw new Error(
        `Lỗi khi truy vấn certificate từ database: ${certError.message}`,
      );
    }

    // Thêm course_name từ courses
    if (certificateData && certificateData.courses) {
      certificateData.course_name = certificateData.courses.name;
    }

    return certificateData;
  } catch (error) {
    console.error("Lỗi khi lấy thông tin certificate:", error);
    return null;
  }
};

/**
 * Lấy tất cả chứng chỉ của người dùng
 */
export const getAllUserCertificates = async (): Promise<Certificate[]> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return [];

    const { data, error } = await supabase
      .from("certificates")
      .select("*, courses(*)")
      .eq("user_id", user.user.id);

    if (error) {
      console.error("Error fetching all user certificates:", error);
      return [];
    }

    return data as Certificate[];
  } catch (error) {
    console.error("Error in getAllUserCertificates:", error);
    return [];
  }
};

/**
 * Xác thực chứng chỉ theo ID
 */
export const verifyCertificate = async (
  certificateId: string,
): Promise<Certificate | null> => {
  try {
    const { data, error } = await supabase
      .from("certificates")
      .select("*, courses(*)")
      .eq("certificate_id", certificateId)
      .eq("is_valid", true)
      .single();

    if (error) {
      console.error("Error verifying certificate:", error);
      return null;
    }

    return data as Certificate;
  } catch (error) {
    console.error("Error in verifyCertificate:", error);
    return null;
  }
};

/**
 * Kiểm tra xem người dùng đã có chứng chỉ cho khóa học chưa
 */
export const hasCertificateForCourse = async (
  courseId: string,
): Promise<boolean> => {
  try {
    const certificate = await getUserCertificate(courseId);
    return !!certificate;
  } catch (error) {
    console.error("Error in hasCertificateForCourse:", error);
    return false;
  }
};
